import React, { useEffect, useState } from 'react';
import Alert from 'components/ui/Alert';
import { Button } from '@cargomatic/ui';
import Dropdown, { SimpleOption } from 'components/v2/dropdowns/CustomDropdown';
import LoadingIndicator from 'components/v2/LoadingIndicator';
import moment from 'moment';
import Label from 'components/v2/forms/Label';
import TextField from 'components/v2/forms/TextField';
import {
  createFscSettings,
  getCargomaticFscConfigTemplate,
  updateFscSettings,
} from 'utils/FscSettingsApiUtils';
import userApiUtils from 'utils/UserWebAPIUtils';
import { useTranslation } from 'react-i18next';
import { showToast } from 'stores/ToastStore';
import ErrorLabel from 'components/v2/forms/ErrorLabel';
import CargomaticTooltip from 'components/v2/Tooltip';
import { isCarrier, isShipper } from 'helpers/roles';
import {
  FSC_TYPE,
  FSC_CALCULATION_FREQUENCY,
  FSC_CONFIG_ACCOUNT_TYPE,
  FSC_EFFECTIVE_DAY,
  FSC_EXPORT_ANCHOR_EVENTS,
  FSC_IMPORT_ANCHOR_EVENTS,
  FSC_REPOSITION_ANCHOR_EVENTS,
  FSC_SOURCE,
  FUEL_PRICE_WEEK,
} from 'constants/fsc/fsc';
import {
  FSC_CALCULATION_FREQUENCY_SLUGS,
  FSC_EFFECTIVE_DAY_SLUGS,
  FSC_EXPORT_ANCHOR_EVENTS_SLUGS,
  FSC_IMPORT_ANCHOR_EVENTS_SLUGS,
  FSC_REPOSITION_ANCHOR_EVENTS_SLUGS,
  FSC_SOURCE_SLUGS,
  FSC_TYPE_SLUGS,
  FUEL_PRICE_WEEK_SLUGS,
} from 'constants/fsc/slugs';
import Panel from 'components/v2/Panel';
import { isNull } from 'lodash';
import FSCPreview from './FSCPreview';

const variableFields = [
  FSC_TYPE.VARIABLE_PERCENT,
  FSC_TYPE.VARIABLE_AMOUNT_PER_MILE,
];

const httpErrors = {
  FSC_SETTING_ALREADY_EXISTS_IN_RANGE: 'FSC_SETTING_ALREADY_EXISTS_IN_RANGE',
};

export default function FSCSettingsForm({
  accountId,
  data,
  saving,
  onChange = () => {},
  onSaveFail = () => {},
  onSaveSuccess = () => {},
  customMode = false, // to create only new fsc config without account id (for example for custom FSC settings in lane rate form)
}) {
  const { t } = useTranslation();
  const [errors, setErrors] = useState({});
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [customerType, setCustomerType] = useState('');
  const [formFields, setFormFields] = useState({
    validityStartDate: '',
    validityEndDate: '',
    fscType: '',
    fscAmountCost: '',
    fscAmountPercent: '',
    matrixSource: '',
    calculationFrequency: '',
    fuelPriceWeek: '',
    effectiveDay: '',
    importAnchorEvent: '',
    exportAnchorEvent: '',
    repositionAnchorEvent: '',
    ...data,
  });

  const [previewFields, setPreviewFields] = useState({
    fscMin: data.fscMin,
    fscStepInterval: data.fscStepInterval,
    fuelPriceMin: data.fuelPriceMin,
    fuelPriceMax: data.fuelPriceMax,
    fuelPriceStepInterval: data.fuelPriceStepInterval,
    fscStepUnit:
      formFields.fscType === FSC_TYPE.VARIABLE_AMOUNT_PER_MILE
        ? t('COST_PER_MILE')
        : '%',
  });

  const [showPreview, setShowPreview] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const [cargomaticTemplateLoading, setCargomaticTemplateLoading] =
    useState(false);
  const [cargomaticConfigTemplate, setCargomaticConfigTemplate] =
    useState(null);
  const [cargomaticTemplateSelected, setCargomaticTemplateSelected] = useState(
    data?.matrixSource === FSC_SOURCE.CARGOMATIC,
  );

  const cargomaticMatrixSourceDisabled =
    formFields.fscType === FSC_TYPE.VARIABLE_AMOUNT_PER_MILE;
  const overrideSettings = cargomaticTemplateSelected && !customMode;

  const variableFuelSurchargeTypes = [
    FSC_TYPE.VARIABLE_PERCENT,
    FSC_TYPE.VARIABLE_AMOUNT_PER_MILE,
  ];

  const matrixSources = [
    {
      name: t(FSC_SOURCE_SLUGS.CARGOMATIC),
      value: FSC_SOURCE.CARGOMATIC,
      disabled: cargomaticMatrixSourceDisabled,
      render: (option, { selected, active, disabled }) => {
        return (
          <div style={{ display: 'flex', flexDirection: 'row' }}>
            <SimpleOption
              name={option.name}
              selected={selected}
              active={active}
              disabled={disabled}
            />
            {cargomaticMatrixSourceDisabled ? (
              <CargomaticTooltip className="ml-1">
                {t('CARGOMATIC_SOURCE_DISABLED_TOOLTIP')}
              </CargomaticTooltip>
            ) : null}
          </div>
        );
      },
    },
    {
      name: t(FSC_SOURCE_SLUGS.REGIONAL),
      value: FSC_SOURCE.REGIONAL,
    },
    {
      name: t(FSC_SOURCE_SLUGS.CUSTOMER_SPECIFIC),
      value: FSC_SOURCE.CUSTOMER_SPECIFIC,
    },
  ];

  const calculationFrequencies = [
    {
      name: t(FSC_CALCULATION_FREQUENCY_SLUGS.WEEKLY),
      value: FSC_CALCULATION_FREQUENCY.WEEKLY,
    },
    {
      name: t(FSC_CALCULATION_FREQUENCY_SLUGS.MONTHLY),
      value: FSC_CALCULATION_FREQUENCY.MONTHLY,
    },
  ];

  const fuelPriceWeekTypes = [
    {
      name: t(FUEL_PRICE_WEEK_SLUGS.CURRENT_WEEK),
      value: FUEL_PRICE_WEEK.CURRENT_WEEK,
    },
    {
      name: t(FUEL_PRICE_WEEK_SLUGS.PREVIOUS_WEEK),
      value: FUEL_PRICE_WEEK.PREVIOUS_WEEK,
    },
  ];

  const effectiveDays = [
    {
      name: t(FSC_EFFECTIVE_DAY_SLUGS.MONDAY),
      value: FSC_EFFECTIVE_DAY.MONDAY,
    },
    {
      name: t(FSC_EFFECTIVE_DAY_SLUGS.TUESDAY),
      value: FSC_EFFECTIVE_DAY.TUESDAY,
    },
    {
      name: t(FSC_EFFECTIVE_DAY_SLUGS.WEDNESDAY),
      value: FSC_EFFECTIVE_DAY.WEDNESDAY,
    },
  ];

  const importAnchorEvents = [
    {
      name: t(FSC_IMPORT_ANCHOR_EVENTS_SLUGS.ORDER_CREATION_DATE),
      value: FSC_IMPORT_ANCHOR_EVENTS.ORDER_CREATION_DATE,
    },
    {
      name: t(FSC_IMPORT_ANCHOR_EVENTS_SLUGS.TENDER_DATE),
      value: FSC_IMPORT_ANCHOR_EVENTS.TENDER_DATE,
    },
    {
      name: t(FSC_IMPORT_ANCHOR_EVENTS_SLUGS.LOADED_PICKUP_DATE),
      value: FSC_IMPORT_ANCHOR_EVENTS.LOADED_PICKUP_DATE,
    },
    {
      name: t(FSC_IMPORT_ANCHOR_EVENTS_SLUGS.LOADED_DELIVERY_DATE),
      value: FSC_IMPORT_ANCHOR_EVENTS.LOADED_DELIVERY_DATE,
    },
    {
      name: t(FSC_IMPORT_ANCHOR_EVENTS_SLUGS.EMPTY_RETURN_DATE),
      value: FSC_IMPORT_ANCHOR_EVENTS.EMPTY_RETURN_DATE,
    },
  ];

  const exportAnchorEvents = [
    {
      name: t(FSC_EXPORT_ANCHOR_EVENTS_SLUGS.ORDER_CREATION_DATE),
      value: FSC_EXPORT_ANCHOR_EVENTS.ORDER_CREATION_DATE,
    },
    {
      name: t(FSC_EXPORT_ANCHOR_EVENTS_SLUGS.TENDER_DATE),
      value: FSC_EXPORT_ANCHOR_EVENTS.TENDER_DATE,
    },
    {
      name: t(FSC_EXPORT_ANCHOR_EVENTS_SLUGS.EMPTY_PICKUP_DATE),
      value: FSC_EXPORT_ANCHOR_EVENTS.EMPTY_PICKUP_DATE,
    },
    {
      name: t(FSC_EXPORT_ANCHOR_EVENTS_SLUGS.LOADED_PICKUP_DATE),
      value: FSC_EXPORT_ANCHOR_EVENTS.LOADED_PICKUP_DATE,
    },
    {
      name: t(FSC_EXPORT_ANCHOR_EVENTS_SLUGS.LOADED_DELIVERY_DATE),
      value: FSC_EXPORT_ANCHOR_EVENTS.LOADED_DELIVERY_DATE,
    },
  ];

  const repositionAnchorEvents = [
    {
      name: t(FSC_REPOSITION_ANCHOR_EVENTS_SLUGS.ORDER_CREATION_DATE),
      value: FSC_REPOSITION_ANCHOR_EVENTS.ORDER_CREATION_DATE,
    },
    {
      name: t(FSC_REPOSITION_ANCHOR_EVENTS_SLUGS.TENDER_DATE),
      value: FSC_REPOSITION_ANCHOR_EVENTS.TENDER_DATE,
    },
    {
      name: t(FSC_REPOSITION_ANCHOR_EVENTS_SLUGS.PICKUP_DATE),
      value: FSC_REPOSITION_ANCHOR_EVENTS.PICKUP_DATE,
    },
    {
      name: t(FSC_REPOSITION_ANCHOR_EVENTS_SLUGS.DELIVERY_DATE),
      value: FSC_REPOSITION_ANCHOR_EVENTS.DELIVERY_DATE,
    },
  ];

  async function loadCargomaticTemplate() {
    if (cargomaticConfigTemplate) {
      setFormFields({
        ...formFields,
        ...cargomaticConfigTemplate,
        matrixSource: FSC_SOURCE.CARGOMATIC,
      });
      return;
    }
    setCargomaticTemplateLoading(true);
    await getCargomaticConfigTemplate();
    setCargomaticTemplateLoading(false);
  }

  async function getCargomaticConfigTemplate() {
    try {
      const template = await getCargomaticFscConfigTemplate(customerType);
      const formattedTemplate = {
        validityStartDate: template.validityStartDate,
        validityEndDate: template.validityEndDate,
        calculationFrequency: template.calculationFrequency,
        fuelPriceWeek: template.fuelPriceWeek,
        effectiveDay: template.effectiveDay,
        importAnchorEvent: template.anchorEventImport,
        exportAnchorEvent: template.anchorEventExport,
        repositionAnchorEvent: template.anchorEventReposition,
        fuelPriceMin: template.matrixFuelPriceMin,
        fuelPriceMax: template.matrixFuelPriceMax,
        fuelPriceStepInterval: template.matrixFuelPriceStepInterval,
        fscMin:
          template.matrixFscMinPercent || template.matrixFscMinCostPerMile,
        fscStepInterval:
          template.matrixFscPercentStepInterval ||
          template.matrixFscCostPerMileStepInterval,
      };
      setCargomaticConfigTemplate(formattedTemplate);
      setFormFields({
        ...formFields,
        ...formattedTemplate,
        matrixSource: FSC_SOURCE.CARGOMATIC,
      });
    } catch (e) {
      setCargomaticConfigTemplate({});
      console.error('CARGOMATIC_FSC_CONFIG_TEMPLATE_ERROR', e);
    }
  }

  function previewDisabled() {
    if (!variableFields.includes(formFields.fscType)) {
      return true;
    }

    const {
      fuelPriceMin,
      fuelPriceMax,
      fuelPriceStepInterval,
      fscMin,
      fscStepInterval,
    } = formFields;

    return !(
      !!fuelPriceMin &&
      !!fuelPriceMax &&
      !!fuelPriceStepInterval &&
      !!fscMin &&
      !!fscStepInterval
    );
  }

  const fscTypeOptions = [
    { value: FSC_TYPE.ALL_IN, name: t(FSC_TYPE_SLUGS.ALL_IN) },
    {
      value: FSC_TYPE.VIA_INTEGRATION,
      name: t(FSC_TYPE_SLUGS.VIA_INTEGRATION),
    },
    { value: FSC_TYPE.FLAT_AMOUNT, name: t(FSC_TYPE_SLUGS.FLAT_AMOUNT) },
    { value: FSC_TYPE.FLAT_PERCENT, name: t(FSC_TYPE_SLUGS.FLAT_PERCENT) },
    {
      value: FSC_TYPE.VARIABLE_PERCENT,
      name: t(FSC_TYPE_SLUGS.VARIABLE_PERCENT),
    },
    {
      value: FSC_TYPE.VARIABLE_AMOUNT_PER_MILE,
      name: t(FSC_TYPE_SLUGS.VARIABLE_AMOUNT_PER_MILE),
    },
  ];

  function validate() {
    const formErrors = {};

    if (!formFields.validityStartDate) {
      formErrors.validityStartDate = t('PLEASE_SET_THE_START_DATE');
    }

    if (!formFields.validityEndDate) {
      formErrors.validityEndDate = t('PLEASE_PROVIDE_THE_END_DATE');
    }

    if (
      new Date(formFields.validityEndDate) <
      new Date(formFields.validityStartDate)
    ) {
      formErrors.validityEndDate = t('VALIDITY_END_DATE_BEFORE_START_DATE');
    }

    if (!formFields.fscType) {
      formErrors.fuelSurchargeType = t('PLEASE_PROVIDE_FUEL_SURCHARGE_TYPE');
    }

    if (
      formFields.fscType === FSC_TYPE.FLAT_AMOUNT &&
      !formFields.fscAmountCost
    ) {
      formErrors.fscAmountCost = t('PLEASE_PROVIDE_FSC_AMOUNT');
    }

    if (Number(formFields.fscAmountCost) < 0) {
      formErrors.fscAmountCost = t('FSC_AMOUNT_SHOULD_BE_GREATER_THAN_0');
    }

    if (
      formFields.fscType === FSC_TYPE.FLAT_PERCENT &&
      !formFields.fscAmountPercent
    ) {
      formErrors.fscAmountPercent = t('PLEASE_PROVIDE_FSC_PERCENT');
    }

    if (Number(formFields.fscAmountPercent) < 0) {
      formErrors.fscAmountPercent = t('FSC_AMOUNT_SHOULD_BE_GREATER_THAN_0');
    }

    let matrixSettingsErrors = {};
    let assessmentSettingsErrors = {};
    if (
      formFields.fscType === FSC_TYPE.VARIABLE_PERCENT ||
      formFields.fscType === FSC_TYPE.VARIABLE_AMOUNT_PER_MILE
    ) {
      matrixSettingsErrors = validateMatrixSettings();
      assessmentSettingsErrors = validateAssessmentSettings();
    }

    return {
      ...formErrors,
      ...matrixSettingsErrors,
      ...assessmentSettingsErrors,
    };
  }

  function validateAssessmentSettings() {
    const assessmentSettingsErrors = {};
    if (!formFields.matrixSource) {
      assessmentSettingsErrors.matrixSource = t('SELECT_MATRIX_SOURCE');
    }
    if (!formFields.calculationFrequency) {
      assessmentSettingsErrors.calculationFrequency = t(
        'SELECT_CALCULATION_FREQUENCY',
      );
    }

    const isShipper = customerType === FSC_CONFIG_ACCOUNT_TYPE.SHIPPER;
    // remain anchor dates only for shippers, see details https://cargomatic.atlassian.net/browse/CARGO-9126
    if (isShipper) {
      if (!formFields.importAnchorEvent) {
        assessmentSettingsErrors.importAnchorEvent = t(
          'SELECT_IMPORT_ANCHOR_EVENT',
        );
      }
      if (!formFields.exportAnchorEvent) {
        assessmentSettingsErrors.exportAnchorEvent = t(
          'SELECT_EXPORT_ANCHOR_EVENT',
        );
      }
      if (!formFields.repositionAnchorEvent) {
        assessmentSettingsErrors.repositionAnchorEvent = t(
          'SELECT_REPOSITION_ANCHOR_EVENT',
        );
      }
    }

    if (formFields.calculationFrequency === FSC_CALCULATION_FREQUENCY.WEEKLY) {
      if (!formFields.fuelPriceWeek) {
        assessmentSettingsErrors.fuelPriceWeek = t('SELECT_FUEL_PRICE_WEEK');
      }
      if (!formFields.effectiveDay) {
        assessmentSettingsErrors.effectiveDay = t('SELECT_EFFECTIVE_DAY');
      }
    }

    return assessmentSettingsErrors;
  }

  function validateMatrixSettings() {
    const matrixSettingsErrors = {};
    if (!formFields.fuelPriceMin) {
      matrixSettingsErrors.fuelPriceMin = t('PLEASE_PROVIDE_FUEL_PRICE_MIN');
    }
    if (
      !isNull(formFields.fuelPriceMin) &&
      Number(formFields.fuelPriceMin) <= 0
    ) {
      matrixSettingsErrors.fuelPriceMin = t(
        'FUEL_PRICE_MIN_SHOULD_BE_GREATER_THAN_0',
      );
    }

    if (!formFields.fuelPriceMax) {
      matrixSettingsErrors.fuelPriceMax = t('PLEASE_PROVIDE_FUEL_PRICE_MAX');
    }

    if (
      !isNull(formFields.fuelPriceMax) &&
      Number(formFields.fuelPriceMax) <= 0
    ) {
      matrixSettingsErrors.fuelPriceMax = t(
        'FUEL_PRICE_MAX_SHOULD_BE_GREATER_THAN_0',
      );
    }
    if (
      !isNull(formFields.fuelPriceMax) &&
      !isNull(formFields.fuelPriceMin) &&
      Number(formFields.fuelPriceMax) <= Number(formFields.fuelPriceMin)
    ) {
      matrixSettingsErrors.fuelPriceMax = t(
        'FUEL_PRICE_MAX_SHOULD_BE_GREATER_THAN_FUEL_PRICE_MIN',
      );
    }

    if (!formFields.fuelPriceStepInterval) {
      matrixSettingsErrors.fuelPriceStepInterval = t(
        'PLEASE_PROVIDE_FUEL_PRICE_STEP_INTERVAL',
      );
    }
    if (
      !isNull(formFields.fuelPriceStepInterval) &&
      Number(formFields.fuelPriceStepInterval) <= 0
    ) {
      matrixSettingsErrors.fuelPriceStepInterval = t(
        'FUEL_PRICE_STEP_INTERVAL_SHOULD_BE_GREATER_THAN_0',
      );
    }

    if (
      formFields.fuelPriceMax &&
      formFields.fuelPriceMin &&
      formFields.fuelPriceStepInterval &&
      formFields.fuelPriceMax - formFields.fuelPriceMin <
        Number(formFields.fuelPriceStepInterval)
    ) {
      matrixSettingsErrors.fuelPriceMax = t(
        'FUEL_PRICE_MAX_MINUS_FUEL_PRICE_MIN_SHOULD_BE_GREATER_OR_EQUAL_THAN_FUEL_PRICE_STEP',
      );
    }

    if (!formFields.fscMin) {
      matrixSettingsErrors.fscMin = t('PLEASE_PROVIDE_FSC_MIN');
    }
    if (!isNull(formFields.fscMin) && Number(formFields.fscMin) <= 0) {
      matrixSettingsErrors.fscMin = t('FSC_MIN_SHOULD_BE_GREATER_THAN_0');
    }

    if (!formFields.fscStepInterval) {
      matrixSettingsErrors.fscStepInterval = t(
        'PLEASE_PROVIDE_FSC_STEP_INTERVAL',
      );
    }
    if (
      !isNull(formFields.fscStepInterval) &&
      Number(formFields.fscStepInterval) <= 0
    ) {
      matrixSettingsErrors.fscStepInterval = t(
        'FSC_STEP_INTERVAL_SHOULD_BE_GREATER_THAN_0',
      );
    }

    return matrixSettingsErrors;
  }

  function hasErrors() {
    return Object.keys(errors).length > 0;
  }

  function renderSuccess() {
    if (hasErrors() || !saveSuccess) {
      return;
    }

    return (
      <Alert
        type="success"
        title={t('THE_SYSTEM_HAS_SAVED_YOUR_CHANGES')}
        className="mb-4"
      />
    );
  }

  function renderErrors() {
    if (Object.keys(errors).length === 0) {
      return null;
    }

    return (
      <Alert
        type="error"
        title={t('FSC_SETTING_VALIDATION_FAILED')}
        className="mb-4"
      >
        <ul>
          {Object.values(errors).map((error, i) => (
            <li key={i}>{error}</li>
          ))}
        </ul>
      </Alert>
    );
  }

  function renderErrorMessage(error) {
    if (!error) {
      return null;
    }
    return <ErrorLabel className="mt-1">{error}</ErrorLabel>;
  }

  function handleChange(name, value) {
    setFormFields({
      ...formFields,
      [name]: value,
    });
    removeContainerError(name);
  }

  async function handleMatrixSourceChange(selected) {
    setFormFields({
      ...formFields,
      matrixSource: selected.value,
    });
    if (selected.value === FSC_SOURCE.CARGOMATIC) {
      setCargomaticTemplateSelected(true);
      loadCargomaticTemplate();
    } else {
      setCargomaticTemplateSelected(false);
    }
    removeContainerError('matrixSource');
  }

  function handleDropdownChange(name) {
    return function (selected) {
      setFormFields({
        ...formFields,
        [name]: selected.value,
      });
      removeContainerError(name);
    };
  }

  function removeContainerError(name) {
    const newErrors = { ...errors };
    delete newErrors[name];
    setErrors(newErrors);
  }

  function constructObjectToSend() {
    const fscConfig = {
      validityStartDate: formFields.validityStartDate,
      validityEndDate: formFields.validityEndDate,
      fuelSurchargeType: formFields.fuelSurchargeType,
      fscType: formFields.fscType,
      accountId,
      customerType,
    };

    if (formFields.fscType === FSC_TYPE.FLAT_AMOUNT) {
      fscConfig.fscAmountCost = Number(formFields.fscAmountCost);
    }
    if (formFields.fscType === FSC_TYPE.FLAT_PERCENT) {
      fscConfig.fscAmountPercent = Number(formFields.fscAmountPercent);
    }

    if (
      formFields.fscType === FSC_TYPE.VARIABLE_PERCENT ||
      formFields.fscType === FSC_TYPE.VARIABLE_AMOUNT_PER_MILE
    ) {
      fscConfig.matrixSource = formFields.matrixSource;
      fscConfig.calculationFrequency = formFields.calculationFrequency;

      // remain anchor dates only for shippers, see details https://cargomatic.atlassian.net/browse/CARGO-9126
      const isShipper = customerType === FSC_CONFIG_ACCOUNT_TYPE.SHIPPER;
      if (isShipper) {
        fscConfig.anchorEventImport = formFields.importAnchorEvent;
        fscConfig.anchorEventExport = formFields.exportAnchorEvent;
        fscConfig.anchorEventReposition = formFields.repositionAnchorEvent;
      } else {
        fscConfig.anchorEventImport =
          FSC_IMPORT_ANCHOR_EVENTS.LOADED_PICKUP_DATE;
        fscConfig.anchorEventExport =
          FSC_EXPORT_ANCHOR_EVENTS.LOADED_PICKUP_DATE;
        fscConfig.anchorEventReposition =
          FSC_REPOSITION_ANCHOR_EVENTS.PICKUP_DATE;
      }

      if (
        formFields.calculationFrequency === FSC_CALCULATION_FREQUENCY.WEEKLY
      ) {
        fscConfig.fuelPriceWeek = formFields.fuelPriceWeek;
        fscConfig.effectiveDay = formFields.effectiveDay;
      }

      fscConfig.matrixFuelPriceMin = Number(formFields.fuelPriceMin);
      fscConfig.matrixFuelPriceMax = Number(formFields.fuelPriceMax);
      fscConfig.matrixFuelPriceStepInterval = Number(
        formFields.fuelPriceStepInterval,
      );

      if (formFields.fscType === FSC_TYPE.VARIABLE_PERCENT) {
        fscConfig.matrixFscMinPercent = Number(formFields.fscMin);
        fscConfig.matrixFscPercentStepInterval = Number(
          formFields.fscStepInterval,
        );
      } else if (formFields.fscType === FSC_TYPE.VARIABLE_AMOUNT_PER_MILE) {
        fscConfig.matrixFscMinCostPerMile = Number(formFields.fscMin);
        fscConfig.matrixFscCostPerMileStepInterval = Number(
          formFields.fscStepInterval,
        );
      }
    }

    return fscConfig;
  }

  async function saveForm() {
    const formErrors = validate();

    setErrors(formErrors);

    if (Object.keys(formErrors).length > 0) {
      onSaveFail();
      return;
    }

    const payload = constructObjectToSend();

    if (customMode) {
      delete payload.accountId;
    }

    try {
      const response = formFields.id
        ? await updateFscSettings(formFields.id, payload, overrideSettings)
        : await createFscSettings(
            { ...payload, fscUserId: accountId },
            overrideSettings,
          );

      showToast(t('FSC_SETTINGS_SAVED'), t('FSC_SETTING_SAVED_SUCCESSFULLY'));

      setSaveSuccess(true);
      onSaveSuccess(response);
    } catch (e) {
      onSaveFail();

      let message = e.message;

      if (
        e.response?.data?.error ===
        httpErrors.FSC_SETTING_ALREADY_EXISTS_IN_RANGE
      ) {
        message = t('FSC_SETTING_ALREADY_EXISTS_IN_RANGE');
      }

      showToast(t('FSC_SETTINGS_SAVE_FAILED'), message, 'error');
    }
  }

  useEffect(() => {
    if (saving) {
      saveForm();
    }
  }, [saving]);

  useEffect(() => {
    async function getUserAndSetCustomerType() {
      const user = await userApiUtils.getV2(accountId);
      if (isCarrier(user)) {
        setCustomerType(FSC_CONFIG_ACCOUNT_TYPE.CARRIER);
      } else if (isShipper(user)) {
        setCustomerType(FSC_CONFIG_ACCOUNT_TYPE.SHIPPER);
      }
    }
    getUserAndSetCustomerType().catch(console.error);
  }, [accountId]);

  useEffect(() => {
    onChange(formFields);
  }, [formFields]);

  function handleFuelSurchargeTypeChange(selected) {
    const { value: fscType } = selected;

    setFormFields({
      ...formFields,
      fscType,
      fscAmountCost: '',
      fscAmountPercent: '',
      matrixSource: '',
    });
    setCargomaticTemplateSelected(false);
    removeContainerError('fuelSurchargeType');
    setErrors({});
  }

  function renderAssessmentSettings() {
    if (!variableFields.includes(formFields.fscType)) {
      return null;
    }

    const isShipper = customerType === FSC_CONFIG_ACCOUNT_TYPE.SHIPPER;

    return (
      <>
        <hr className="my-5" />
        <h3 className="mb-2">{t('ASSESSMENT_SETTINGS')}</h3>
        {!isShipper && (
          <div className="mb-4 text-gray-400">
            {t('FSC_FUEL_RATED_BASED_ON_PICKUP_DATES')}
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <div className="mb-4">
            <Label htmlFor="matrixSource">
              <div style={{ display: 'flex', flexDirection: 'row' }}>
                <span>{t('MATRIX_SOURCE')}</span>
                <CargomaticTooltip className="ml-1">
                  {t('MATRIX_SOURCE_TOOLTIP')}
                </CargomaticTooltip>
              </div>
            </Label>
            <Dropdown
              id="matrixSource"
              name="matrixSource"
              disabled={cargomaticTemplateLoading}
              options={matrixSources}
              value={matrixSources.find(
                (option) => option.value === formFields.matrixSource,
              )}
              className="mt-1"
              onChange={(selected) => handleMatrixSourceChange(selected)}
              valueKey="value"
              testDataId="matrix-source-dropdown"
            />
            {renderErrorMessage(errors.matrixSource)}
          </div>
          <div className="mb-4">
            <Label htmlFor="calculationFrequency">
              <div style={{ display: 'flex', flexDirection: 'row' }}>
                <span>{t('CALCULATION_FREQUENCY')}</span>
                <CargomaticTooltip className="ml-1">
                  {t('CALCULATION_FREQUENCY_TOOLTIP')}
                </CargomaticTooltip>
              </div>
            </Label>
            <Dropdown
              id="calculationFrequency"
              name="calculationFrequency"
              options={calculationFrequencies}
              value={calculationFrequencies.find(
                (option) => option.value === formFields.calculationFrequency,
              )}
              disabled={cargomaticTemplateSelected}
              className="mt-1"
              onChange={handleDropdownChange('calculationFrequency')}
              valueKey="value"
              testDataId="calculation-frequency-dropdown"
            />
            {renderErrorMessage(errors.calculationFrequency)}
          </div>
          {formFields.calculationFrequency === 'weekly' && (
            <>
              <div className="mb-4">
                <Label htmlFor="fuelPriceWeek">
                  <div style={{ display: 'flex', flexDirection: 'row' }}>
                    <span>{t('FUEL_PRICE_WEEK')}</span>
                    <CargomaticTooltip className="ml-1">
                      {t('FUEL_PRICE_WEEK_TOOLTIP')}
                    </CargomaticTooltip>
                  </div>
                </Label>
                <Dropdown
                  id="fuelPriceWeek"
                  name="fuelPriceWeek"
                  options={fuelPriceWeekTypes}
                  value={fuelPriceWeekTypes.find(
                    (option) => option.value === formFields.fuelPriceWeek,
                  )}
                  disabled={cargomaticTemplateSelected}
                  className="mt-1"
                  onChange={handleDropdownChange('fuelPriceWeek')}
                  valueKey="value"
                  testDataId="fuel-price-week-dropdown"
                />
                {renderErrorMessage(errors.fuelPriceWeek)}
              </div>
              <div className="mb-4">
                <Label htmlFor="effectiveDay">
                  <div style={{ display: 'flex', flexDirection: 'row' }}>
                    <span>{t('EFFECTIVE_DAY')}</span>
                    <CargomaticTooltip className="ml-1">
                      {t('EFFECTIVE_DAY_TOOLTIP')}
                    </CargomaticTooltip>
                  </div>
                </Label>
                <Dropdown
                  id="effectiveDay"
                  name="effectiveDay"
                  options={effectiveDays}
                  value={effectiveDays.find(
                    (option) => option.value === formFields.effectiveDay,
                  )}
                  disabled={cargomaticTemplateSelected}
                  className="mt-1"
                  onChange={handleDropdownChange('effectiveDay')}
                  valueKey="value"
                  testDataId="effective-day-dropdown"
                />
                {renderErrorMessage(errors.effectiveDay)}
              </div>
            </>
          )}
          {/* remain anchor dates only for shippers, see details https://cargomatic.atlassian.net/browse/CARGO-9126 */}
          {isShipper && (
            <>
              <div className="mb-4">
                <Label htmlFor="effectiveDay">
                  <div style={{ display: 'flex', flexDirection: 'row' }}>
                    <span>{t('ANCHOR_EVENT_IMPORT')}</span>
                    <CargomaticTooltip className="ml-1">
                      {t('ANCHOR_EVENT_IMPORT_TOOLTIP')}
                    </CargomaticTooltip>
                  </div>
                </Label>
                <Dropdown
                  id="importAnchorEvent"
                  name="importAnchorEvent"
                  options={importAnchorEvents}
                  value={importAnchorEvents.find(
                    (option) => option.value === formFields.importAnchorEvent,
                  )}
                  disabled={cargomaticTemplateSelected}
                  className="mt-1"
                  onChange={handleDropdownChange('importAnchorEvent')}
                  valueKey="value"
                  testDataId="anchor-event-import-dropdown"
                />
                {renderErrorMessage(errors.importAnchorEvent)}
              </div>
              <div className="mb-4">
                <Label htmlFor="effectiveDay">
                  <div style={{ display: 'flex', flexDirection: 'row' }}>
                    <span>{t('ANCHOR_EVENT_EXPORT')}</span>
                    <CargomaticTooltip className="ml-1">
                      {t('ANCHOR_EVENT_EXPORT_TOOLTIP')}
                    </CargomaticTooltip>
                  </div>
                </Label>
                <Dropdown
                  id="exportAnchorEvent"
                  name="exportAnchorEvent"
                  options={exportAnchorEvents}
                  value={exportAnchorEvents.find(
                    (option) => option.value === formFields.exportAnchorEvent,
                  )}
                  disabled={cargomaticTemplateSelected}
                  className="mt-1"
                  onChange={handleDropdownChange('exportAnchorEvent')}
                  valueKey="value"
                  testDataId="anchor-event-export-dropdown"
                />
                {renderErrorMessage(errors.exportAnchorEvent)}
              </div>
              <div className="mb-4">
                <Label htmlFor="effectiveDay">
                  <div style={{ display: 'flex', flexDirection: 'row' }}>
                    <span>{t('ANCHOR_EVENT_REPOSITION')}</span>
                    <CargomaticTooltip className="ml-1">
                      {t('ANCHOR_EVENT_REPOSITION_TOOLTIP')}
                    </CargomaticTooltip>
                  </div>
                </Label>
                <Dropdown
                  id="repositionAnchorEvent"
                  name="repositionAnchorEvent"
                  options={repositionAnchorEvents}
                  value={repositionAnchorEvents.find(
                    (option) =>
                      option.value === formFields.repositionAnchorEvent,
                  )}
                  disabled={cargomaticTemplateSelected}
                  className="mt-1"
                  onChange={handleDropdownChange('repositionAnchorEvent')}
                  valueKey="value"
                  testDataId="anchor-event-reposition-dropdown"
                />
                {renderErrorMessage(errors.repositionAnchorEvent)}
              </div>
            </>
          )}
        </div>
      </>
    );
  }

  function renderMatrixSettings() {
    if (!variableFields.includes(formFields.fscType)) {
      return null;
    }

    const unit =
      formFields.fscType === FSC_TYPE.VARIABLE_AMOUNT_PER_MILE
        ? `(${t('COST_PER_MILE')})`
        : '(%)';
    const previewButtonDisabled = previewDisabled();

    return (
      <>
        <hr className="my-5" />
        <h3 className="mb-4">{t('MATRIX_SETTINGS')}</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <div className="mb-4">
            <Label htmlFor="fuelPriceMin">
              <div style={{ display: 'flex', flexDirection: 'row' }}>
                <span>{t('FUEL_PRICE_MIN')}</span>
                <CargomaticTooltip className="ml-1">
                  {t('FUEL_PRICE_MIN_TOOLTIP')}
                </CargomaticTooltip>
              </div>
            </Label>
            <TextField
              type="number"
              id="fuelPriceMin"
              name="fuelPriceMin"
              className="mt-1"
              value={formFields.fuelPriceMin}
              onChange={(value) =>
                handleChange('fuelPriceMin', value ? Number(value) : value)
              }
              disabled={cargomaticTemplateSelected}
            />
            {renderErrorMessage(errors.fuelPriceMin)}
          </div>
          <div className="mb-4">
            <Label htmlFor="fuelPriceMax">
              <div style={{ display: 'flex', flexDirection: 'row' }}>
                <span>{t('FUEL_PRICE_MAX')}</span>
                <CargomaticTooltip className="ml-1">
                  {t('FUEL_PRICE_MAX_TOOLTIP')}
                </CargomaticTooltip>
              </div>
            </Label>
            <TextField
              type="number"
              id="fuelPriceMax"
              name="fuelPriceMax"
              className="mt-1"
              value={formFields.fuelPriceMax}
              onChange={(value) =>
                handleChange('fuelPriceMax', value ? Number(value) : value)
              }
              disabled={cargomaticTemplateSelected}
            />
            {renderErrorMessage(errors.fuelPriceMax)}
          </div>
          <div className="mb-4">
            <Label htmlFor="fuelPriceStepInterval">
              <div style={{ display: 'flex', flexDirection: 'row' }}>
                <span>{t('FUEL_PRICE_STEP_INTERVAL')}</span>
                <CargomaticTooltip className="ml-1">
                  {t('FUEL_PRICE_STEP_INTERVAL_TOOLTIP')}
                </CargomaticTooltip>
              </div>
            </Label>
            <TextField
              type="number"
              id="fuelPriceStepInterval"
              name="fuelPriceStepInterval"
              className="mt-1"
              value={formFields.fuelPriceStepInterval}
              onChange={(value) =>
                handleChange(
                  'fuelPriceStepInterval',
                  value ? Number(value) : value,
                )
              }
              disabled={cargomaticTemplateSelected}
            />
            {renderErrorMessage(errors.fuelPriceStepInterval)}
          </div>
          <div className="mb-4">
            <Label htmlFor="fuelPriceMinAmount">
              <div style={{ display: 'flex', flexDirection: 'row' }}>
                <span>
                  {t('FSC_MIN')} {unit}
                </span>
                <CargomaticTooltip className="ml-1">
                  {t('FSC_MIN_TOOLTIP')}
                </CargomaticTooltip>
              </div>
            </Label>
            <TextField
              type="number"
              id="fscMin"
              name="fscMin"
              className="mt-1"
              value={formFields.fscMin}
              onChange={(value) =>
                handleChange('fscMin', value ? Number(value) : value)
              }
              disabled={cargomaticTemplateSelected}
            />
            {renderErrorMessage(errors.fscMin)}
          </div>
          <div className="mb-4">
            <Label htmlFor="fscStepInterval">
              <div style={{ display: 'flex', flexDirection: 'row' }}>
                <span>
                  {t('FSC_UNIT_STEP_INTERVAL')} {unit}
                </span>
                <CargomaticTooltip className="ml-1">
                  {t('FSC_UNIT_STEP_INTERVAL_TOOLTIP')}
                </CargomaticTooltip>
              </div>
            </Label>
            <TextField
              type="number"
              id="fscStepInterval"
              name="fscStepInterval"
              className="mt-1"
              value={formFields.fscStepInterval}
              onChange={(value) =>
                handleChange('fscStepInterval', value ? Number(value) : value)
              }
              disabled={cargomaticTemplateSelected}
            />
            {renderErrorMessage(errors.fscStepInterval)}
          </div>
        </div>
        <div className="flex justify-end mb-4">
          <Button
            variant="outline"
            disabled={previewButtonDisabled}
            onClick={handleShowPreview}
          >
            {loadingPreview && (
              <div className="flex space-x-1 items-center">
                <LoadingIndicator size="small" />
                <span>{t('LOADING')}</span>
              </div>
            )}
            {!loadingPreview && <span>{t('PREVIEW')}</span>}
          </Button>
        </div>
      </>
    );
  }

  function isVariableFSCType() {
    return variableFuelSurchargeTypes.includes(formFields.fscType);
  }

  function renderVariableAlert() {
    if (!isVariableFSCType()) {
      return null;
    }

    return (
      <Panel className="mt-4 mb-4">
        <Alert type="warning">{t('PLEASE_SET_EIA')}</Alert>
      </Panel>
    );
  }

  async function fetchPreview() {
    const matrixSettingsErrors = validateMatrixSettings();
    setErrors(matrixSettingsErrors);
    if (Object.keys(matrixSettingsErrors).length > 0) {
      return;
    }

    setLoadingPreview(true);
    setShowPreview(false);

    setPreviewFields({
      fuelPriceMin: Number(formFields.fuelPriceMin),
      fuelPriceMax: Number(formFields.fuelPriceMax),
      fuelPriceStepInterval: Number(formFields.fuelPriceStepInterval),
      fscMin: Number(formFields.fscMin),
      fscStepInterval: Number(formFields.fscStepInterval),
      fscStepUnit:
        formFields.fscType === FSC_TYPE.VARIABLE_AMOUNT_PER_MILE
          ? t('COST_PER_MILE')
          : '%',
    });

    setLoadingPreview(false);
    setShowPreview(true);
  }

  function renderPreview() {
    if (!variableFields.includes(formFields.fscType)) {
      return null;
    }

    if (loadingPreview) {
      return null;
    }

    if (!showPreview) {
      return null;
    }

    return <FSCPreview data={previewFields} />;
  }

  function handleShowPreview() {
    fetchPreview();
  }

  function getDate(value) {
    if (!value) {
      return '';
    }

    return moment(value).format('YYYY-MM-DD');
  }

  return (
    <>
      {renderSuccess()}
      {renderErrors()}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <div>
          <Label htmlFor="validityStartDate">{t('VALIDITY_START_DATE')}</Label>
          <TextField
            name="validityStartDate"
            className="mt-1"
            id="validityStartDate"
            type="date"
            onChange={(value) => handleChange('validityStartDate', value)}
            value={getDate(formFields.validityStartDate)}
            disabled={cargomaticTemplateSelected}
          />
          {renderErrorMessage(errors.validityStartDate)}
        </div>
        <div>
          <Label htmlFor="validityEndDate">{t('VALIDITY_END_DATE')}</Label>
          <TextField
            name="validityEndDate"
            className="mt-1"
            id="validityEndDate"
            type="date"
            onChange={(value) => handleChange('validityEndDate', value)}
            value={getDate(formFields.validityEndDate)}
            disabled={cargomaticTemplateSelected}
          />
          {renderErrorMessage(errors.validityEndDate)}
        </div>
        <div className="">
          <Label htmlFor="fuelSurchargeType">{t('FUEL_SURCHARGE_TYPE')}</Label>
          <Dropdown
            id="fuelSurchargeType"
            options={fscTypeOptions}
            disabled={cargomaticTemplateLoading}
            className="mt-1"
            onChange={handleFuelSurchargeTypeChange}
            value={fscTypeOptions.find(
              (option) => option.value === formFields.fscType,
            )}
            valueKey="value"
            testDataId="fuel-surcharge-type-dropdown"
          />
          {renderErrorMessage(errors.fuelSurchargeType)}
        </div>
        {formFields.fscType === FSC_TYPE.FLAT_AMOUNT && (
          <div className="">
            <Label htmlFor="fscAmountCost">{t('FSC_AMOUNT_COST')}</Label>
            <TextField
              type="number"
              name="fscAmountCost"
              id="fscAmountCost"
              className="mt-1"
              value={formFields.fscAmountCost}
              placeholder={t('FSC_AMOUNT')}
              onChange={(value) =>
                handleChange('fscAmountCost', value ? Number(value) : value)
              }
            />
            {renderErrorMessage(errors.fscAmountCost)}
          </div>
        )}
        {formFields.fscType === FSC_TYPE.FLAT_PERCENT && (
          <div className="">
            <Label htmlFor="fscAmountPercent">{t('FSC_AMOUNT_PERCENT')}</Label>
            <TextField
              type="number"
              name="fscAmountPercent"
              id="fscAmountPercent"
              className="mt-1"
              value={formFields.fscAmountPercent}
              placeholder={t('FSC_PERCENT')}
              onChange={(value) =>
                handleChange('fscAmountPercent', value ? Number(value) : value)
              }
              step={1}
              max={100}
            />
            {renderErrorMessage(errors.fscAmountPercent)}
          </div>
        )}
      </div>
      {renderVariableAlert()}
      {renderAssessmentSettings()}
      {renderMatrixSettings()}
      {renderPreview()}

      {overrideSettings ? (
        <Alert type="warning" title={t('ATTENTION')} className="mt-1">
          <p>{t('OVERRIDE_WARNING')}</p>
        </Alert>
      ) : null}
    </>
  );
}
